/**
 * First run and the install page.
 *
 * These screens carry the legitimacy requirements from the design phase, and they
 * exist because of a specific research finding: the least confident participant
 * would not have completed "type an unfamiliar address, then add to home screen"
 * alone, and said plainly that being asked to do so is exactly what she has been
 * warned never to do. So: the company is named, the domain is explained, there is
 * no urgency anywhere, the install steps are spelled out per browser, and the
 * first suggested capture is a wifi password rather than a passport.
 */

import { el, mount } from './ui.ts';
import { setMeta } from './store.ts';

type Browser = 'ios-safari' | 'android-chrome' | 'desktop';

export function detectBrowser(ua = navigator.userAgent): Browser {
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios-safari';
  if (/Android/.test(ua)) return 'android-chrome';
  return 'desktop';
}

export function isInstalled(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as { standalone?: boolean }).standalone === true;
}

const INSTALL_STEPS: Record<Browser, string[]> = {
  'ios-safari': [
    'Tap the Share button at the bottom of Safari — the square with an arrow coming out of it.',
    'Scroll down the list and tap "Add to Home Screen".',
    'Tap "Add" in the top corner. Mayfly appears on your home screen like any other app.',
  ],
  'android-chrome': [
    'Tap the three dots at the top right of Chrome.',
    'Tap "Add to Home screen", then "Install".',
    'Mayfly appears in your app list like any other app.',
  ],
  desktop: [
    'Look for the install icon in your browser address bar, or open the browser menu and choose "Install Mayfly".',
    'Mayfly works in a normal browser tab too — installing just gives it its own window.',
  ],
};

export async function showFirstRun(onDone: () => void): Promise<void> {
  let step = 0;

  const cards: (() => Node)[] = [
    () =>
      el('div', { class: 'card-page' }, [
        el('h2', { text: 'A drawer for photos that are not memories' }),
        el('p', {
          text: 'The parking bay. The wifi password on the router. The whiteboard after the '
            + 'meeting. The part number. Photos you need for a bit, and then never again.',
        }),
        el('p', { class: 'hint', text: 'They stay out of your camera roll entirely.' }),
      ]),
    () =>
      el('div', { class: 'card-page' }, [
        el('h2', { text: 'Nothing here is kept by default' }),
        el('p', {
          text: 'You say what a photo is — Parking, Wifi, Notes — and Mayfly works out how long '
            + 'to keep it. It always tells you what it decided.',
        }),
        el('p', {
          text: 'When something matters, tap Keep and it stays until you say otherwise. '
            + 'Receipts and documents belong there.',
        }),
        el('p', {
          class: 'warn-note',
          text: 'Everything else really does go, and there is no bin to fish it back out of. '
            + 'Mayfly flags anything close to going whenever you open it — but it cannot warn '
            + 'you while it is closed, because nothing runs in a browser you have shut.',
        }),
      ]),
    () =>
      el('div', { class: 'card-page' }, [
        el('h2', { text: 'It stays on your phone' }),
        el('p', {
          text: 'Photos are encrypted on your device. There is no account, no email address and '
            + 'no sign-in, and your photos are never uploaded anywhere.',
        }),
        el('p', {
          text: 'Mayfly works with no signal at all — in a loft, a cellar, or a basement car park.',
        }),
        // The single most important thing to say before someone photographs
        // anything they would mind losing.
        el('p', {
          class: 'warn-note',
          text: 'This is the only copy. Photos live in this browser on this device — not in the '
            + 'cloud, and not on your other phone. Clearing your browser data removes them, and '
            + 'in a private window they go when you close the tab.',
        }),
        el('p', { class: 'hint', text: 'Try it on something harmless first, like a wifi password.' }),
      ]),
  ];

  const render = () => {
    mount(
      el('div', { class: 'onboard' }, [
        el('div', { class: 'dots' }, cards.map((_, i) =>
          el('span', { class: i === step ? 'dot on' : 'dot', 'aria-hidden': 'true' }))),
        cards[step](),
        el('div', { class: 'onboard-actions' }, [
          step > 0
            ? el('button', { class: 'ghost', text: 'Back', onclick: () => { step--; render(); } })
            : el('span'),
          el('button', {
            class: 'primary',
            text: step === cards.length - 1 ? 'Take your first photo' : 'Next',
            onclick: async () => {
              if (step < cards.length - 1) { step++; render(); return; }
              await setMeta('onboarded', true);
              onDone();
            },
          }),
        ]),
        !isInstalled() ? installHint() : el('span'),
      ]),
    );
  };
  render();
}

function installHint(): HTMLElement {
  const browser = detectBrowser();
  return el('details', { class: 'install' }, [
    el('summary', { text: 'Add Mayfly to your home screen' }),
    el('ol', {}, INSTALL_STEPS[browser].map((s) => el('li', { text: s }))),
    el('p', { class: 'hint', text: 'It works in a browser tab as well — installing just makes it quicker to reach.' }),
    provenance(),
  ]);
}

/**
 * Who we are, in plain words. Four of six research participants read the address as
 * a phishing link, and the two most security-aware said they would be right to
 * refuse it. An unexplained domain is what reads as fraudulent, so it is explained.
 */
export function provenance(): HTMLElement {
  return el('div', { class: 'provenance' }, [
    el('p', {
      text: 'Mayfly is made by Hikon Limited, a company registered in England and Wales. '
        + 'nokih.com is Hikon’s own domain, which is why Mayfly lives at a web address under it.',
    }),
    el('p', {
      text: 'We will never ask you for a password, a payment or a code. If anything claiming to '
        + 'be Mayfly does, it is not us.',
    }),
  ]);
}
